import React, { use, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import AddEditSeriesDialog from './add-edit-series-dialog';
import { Context } from './shared/context';
import { IFormData, IFormDataAxisNumericSeries, IListItem, INumericThreshold, IRowDataRule } from './shared/interfaces';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import { Icon } from '@components-clean/core/icons/icon';
import { TextBoxController } from '@components-clean/core/text-box';
import { FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import {
    TimelineAxisType,
    TimelineDateSeriesType,
    TimelineFieldLogicalOperator,
    TimelineNumericSeriesType,
} from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

interface IProps {
    side: 'left' | 'right';
    control: Control<IFormData>;
    setValue: UseFormSetValue<IFormData>;
    isEditing: boolean;
}

const seriesTypeToIconMap: Record<string, FontAwesomeIconProps['icon']> = {
    [TimelineDateSeriesType.SINGLE_DATE]: ['far', 'timeline'],
    [TimelineDateSeriesType.DATE_RANGE]: ['far', 'chart-gantt'],
    [TimelineNumericSeriesType.LINE]: ['far', 'chart-line'],
    [TimelineNumericSeriesType.SCATTER]: ['far', 'chart-scatter'],
} as const;

const AxisCard = ({ side, control, setValue, isEditing }: IProps) => {
    const { listItems } = use(Context);

    const { t: tTimeline } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.timeline' });
    const { t } = useTranslation('admin', { keyPrefix: `managePanels.panelDetails.timeline.${side}Axis` });
    const { t: tSeries } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.timeline.series' });
    const [editingSeriesIndex, setEditingSeriesIndex] = useState<number | null>(null);

    const type = useWatch({ control, name: `${side}Axis.type` });
    const series = useWatch({ control, name: `${side}Axis.series` });

    const otherLabel = useWatch({ control, name: `${side === 'left' ? 'right' : 'left'}Axis.label` });

    const isReadOnly = !isEditing || type.id === TimelineAxisType.NONE;

    const getEditingSeries = (seriesIndex: number) => {
        return seriesIndex < series.length ? series[seriesIndex] : undefined;
    };

    return (
        <>
            {editingSeriesIndex !== null && (
                <AddEditSeriesDialog
                    formType={type.id as TimelineAxisType}
                    series={getEditingSeries(editingSeriesIndex)}
                    otherSeriesLabels={
                        new Set(
                            series
                                .filter((s) => s !== getEditingSeries(editingSeriesIndex) && !!s.label)
                                .map((s) => s.label!.trim().toLowerCase()),
                        )
                    }
                    isEditing={isEditing}
                    onClose={() => setEditingSeriesIndex(null)}
                    onSubmit={(isNew, savedSeries) => {
                        const newSeries = [...series];

                        if (isNew) {
                            newSeries.push(savedSeries);
                        } else {
                            newSeries[editingSeriesIndex] = savedSeries;
                        }

                        setValue(`${side}Axis.series`, newSeries, { shouldDirty: true });
                        setEditingSeriesIndex(null);
                    }}
                />
            )}

            <Card
                title={t('title')}
                data-testid={`${side}-axis-card`}
                className="axis-card"
                headerActions={
                    <div className="axis-type-wrapper" style={{ position: 'relative', top: '-3px' }}>
                        <BasicDropDownController
                            data-testid="axis-type-dropdown"
                            name={`${side}Axis.type`}
                            control={control}
                            readOnly={!isEditing}
                            cardHeader
                            textField="text"
                            data={listItems.axisTypes.filter(
                                (item) => side === 'right' || item.id !== TimelineAxisType.NONE,
                            )}
                            onChange={() => {
                                // Clear down the series on change.
                                setValue(`${side}Axis.series`, [], { shouldDirty: true });
                            }}
                        />
                    </div>
                }
            >
                {(type?.id === TimelineAxisType.NUMERIC || type?.id === TimelineAxisType.DATE) && (
                    <>
                        <div className="label-and-unit-area">
                            <TextBoxController
                                label={t('label')}
                                data-testid="label"
                                name={`${side}Axis.label`}
                                control={control}
                                readOnly={isReadOnly}
                                required
                                maxLength={30}
                                validateRules={{
                                    uniqueAxisLabel: (a: string | null) => {
                                        if (!a || !otherLabel) {
                                            return true;
                                        }
                                        if (a.trim().toLowerCase() === otherLabel.trim().toLowerCase()) {
                                            return tTimeline('validationErrors.uniqueAxisLabels');
                                        }

                                        return true;
                                    },
                                }}
                            />
                            <TextBoxController
                                label={t('unit')}
                                data-testid="unit"
                                name={`${side}Axis.unit`}
                                control={control}
                                readOnly={isReadOnly}
                                maxLength={10}
                            />
                        </div>

                        <div className="series-area">
                            {Array(4)
                                .fill(null)
                                .map((_, index) => {
                                    let content;
                                    let className = 'empty';

                                    if (index === series.length) {
                                        className = 'add';
                                        content = (
                                            <Button
                                                className="add-button"
                                                data-testid="add-button"
                                                disabled={!isEditing}
                                                onClick={() => setEditingSeriesIndex(index)}
                                            >
                                                <FontAwesomeIcon icon={['far', 'plus']} />
                                            </Button>
                                        );
                                    } else if (index < series.length) {
                                        className = 'existing';
                                        const seriesItem = series[index];
                                        const threshold = (seriesItem as IFormDataAxisNumericSeries)?.threshold;

                                        const getThresholdDisplayText = (numericThreshold: INumericThreshold) => {
                                            let upperValue;
                                            let lowerValue;

                                            if (!numericThreshold) {
                                                return;
                                            }

                                            if (numericThreshold.field) {
                                                if (numericThreshold.field.upper) {
                                                    upperValue =
                                                        listItems.fields.find(
                                                            (f) => f.id === numericThreshold.field!.upper!.id,
                                                        )?.text || '[?]';
                                                }
                                                if (numericThreshold.field.lower) {
                                                    lowerValue =
                                                        listItems.fields.find(
                                                            (f) => f.id === numericThreshold.field!.lower!.id,
                                                        )?.text || '[?]';
                                                }
                                            }
                                            if (numericThreshold.value) {
                                                if (numericThreshold.value.upper !== null) {
                                                    upperValue = bankersRound(numericThreshold.value.upper, 2);
                                                }
                                                if (numericThreshold.value.lower !== null) {
                                                    lowerValue = bankersRound(numericThreshold.value.lower, 2);
                                                }
                                            }

                                            const lines = [];
                                            if (upperValue !== undefined) {
                                                lines.push(`${tSeries('list.thresholdTooltip.upper')} : ${upperValue}`);
                                            }
                                            if (lowerValue !== undefined) {
                                                lines.push(`${tSeries('list.thresholdTooltip.lower')} : ${lowerValue}`);
                                            }
                                            return lines.join('\n');
                                        };

                                        const getRuleDisplayText = (rule: IRowDataRule) => {
                                            const lines = [`${rule.field1?.text} ${rule.operator1?.text}`];

                                            const renderValue = (
                                                operator?: IListItem | null,
                                                fieldType?: FieldDefinitionFieldTypeDto,
                                                value?: string | IListItem | Date | null,
                                            ) => {
                                                if (!operator || !fieldType || !value) return '';

                                                if (
                                                    operator.id !== TimelineFieldLogicalOperator.IS_BLANK &&
                                                    operator.id !== TimelineFieldLogicalOperator.IS_NOT_BLANK
                                                ) {
                                                    switch (fieldType) {
                                                        case 'BOOL':
                                                            return ` ${value ? (value as IListItem).text : '[?]'}`;
                                                        case 'DATE':
                                                        case 'PARTIAL_DATE':
                                                            return ` ${stringDateWithoutTimeFromUtc(value?.toString())}`;
                                                        case 'TEXT':
                                                            return ` "${value}"`;
                                                        default:
                                                            return ` ${value}`;
                                                    }
                                                }
                                            };

                                            lines[0] += renderValue(
                                                rule.operator1!,
                                                rule.field1!.fieldType,
                                                rule.value1!,
                                            );

                                            if (rule.logicalOperator) {
                                                lines.push(rule.logicalOperator.text);

                                                lines.push(`${rule.field2?.text} ${rule.operator2?.text}`);

                                                lines[2] += renderValue(
                                                    rule.operator2,
                                                    rule.field2?.fieldType,
                                                    rule.value2,
                                                );
                                            }

                                            return lines.join('\n');
                                        };

                                        const thresholdDisplayText = getThresholdDisplayText(threshold);

                                        // Map series type to icon. Use LINE if unable to map
                                        const seriesIconProp =
                                            seriesTypeToIconMap[seriesItem.type.id] ??
                                            seriesTypeToIconMap[TimelineNumericSeriesType.LINE];

                                        content = (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={seriesIconProp}
                                                    size="lg"
                                                    className="series-icon"
                                                />

                                                <label htmlFor="series">{seriesItem.label}</label>

                                                {thresholdDisplayText && (
                                                    <div className="icon-tooltip-wrapper">
                                                        <Icon
                                                            dataTestId="thresholds-icon"
                                                            icon={['far', 'xmarks-lines']}
                                                            title={thresholdDisplayText}
                                                            padding="none"
                                                            tooltipPosition="left"
                                                        />
                                                    </div>
                                                )}

                                                {seriesItem.rowData.rule && (
                                                    <div className="icon-tooltip-wrapper">
                                                        <Icon
                                                            dataTestId="rules-icon"
                                                            icon={['fas', 'filter']}
                                                            title={getRuleDisplayText(seriesItem.rowData.rule)}
                                                            padding="none"
                                                            tooltipPosition="left"
                                                        />
                                                    </div>
                                                )}

                                                <Button
                                                    className="edit-button"
                                                    data-testid="edit-button"
                                                    disabled={!isEditing}
                                                    onClick={() => setEditingSeriesIndex(index)}
                                                >
                                                    <FontAwesomeIcon icon={['fal', 'pen-to-square']} />
                                                </Button>
                                                <Button
                                                    className="delete-button"
                                                    data-testid="delete-button"
                                                    disabled={!isEditing}
                                                    onClick={() => {
                                                        const newSeries = [...series];
                                                        newSeries.splice(index, 1);
                                                        setValue(`${side}Axis.series`, newSeries, {
                                                            shouldDirty: true,
                                                        });
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={['fas', 'trash-xmark']} />
                                                </Button>
                                            </>
                                        );
                                    }

                                    return (
                                        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                                        // eslint-disable-next-line @eslint-react/no-array-index-key
                                        <div className={`series ${className}`} key={`series-${index}`}>
                                            {content}
                                        </div>
                                    );
                                })}
                        </div>
                    </>
                )}
            </Card>
        </>
    );
};

export default AxisCard;
